/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { getVendorStatus } from '../../services/subscriptionService';
import { useVendorAnalytics } from '../../services/vendorAnalyticsService';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import RenewalModal from '../../components/shared/RenewalModal';
import SubscriptionDetailsModal from '../../components/vendor/SubscriptionDetailsModal';
import CustomSelect from '../../components/shared/CustomSelect';
import AnnouncementCard from '../../components/shared/AnnouncementCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { 
  Calendar, 
  DollarSign,
  Star,
  Crown,
  Bell,
  ArrowRight,
  Clock,
  Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';

const VendorDashboard = () => {
  const { user } = useAuth();
  const { hasActiveSubscription, isChecking, checkSubscriptionStatus } = useSubscriptionStatus();
  const navigate = useNavigate();
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [period, setPeriod] = useState('month');

  // Fetch detailed subscription information
  const fetchSubscriptionDetails = async () => {
    if (!user || user.user_type !== 'vendor') return;
    
    try {
      setLoading(true);
      const response = await getVendorStatus();
      if (response.success && response.data.current_subscription) {
        setSubscriptionDetails(response.data.current_subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics
  const { data: analyticsData } = useVendorAnalytics(period);
  const analytics = analyticsData?.data || {};

  // Fetch announcements
  const { data: announcementsData } = useAnnouncements();
  const announcements = announcementsData?.data || [];

  // Fetch recent bookings
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['vendorRecentBookings'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings?per_page=5&sort_by=created_at&sort_order=desc');
      return response.data;
    }
  });

  const recentBookings = bookingsData?.data?.data || bookingsData?.data || [];

  // Fetch booking statistics
  const { data: statsData } = useQuery({
    queryKey: ['vendorBookingStats'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings/statistics');
      return response.data;
    }
  });

  const stats = statsData?.data || {};

  // Fetch subscription details when component mounts or subscription status changes
  useEffect(() => {
    if (hasActiveSubscription === true) {
      fetchSubscriptionDetails();
    }
  }, [hasActiveSubscription, user?.id]);

  // Helper functions
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if subscription is expiring soon
  const isExpiringSoon = () => {
    if (!subscriptionDetails?.days_remaining) return false;
    return subscriptionDetails.days_remaining <= 5 && subscriptionDetails.days_remaining > 0;
  };

  const getUrgencyLevel = () => {
    if (!subscriptionDetails?.days_remaining) return null;
    if (subscriptionDetails.days_remaining <= 1) return 'critical';
    if (subscriptionDetails.days_remaining <= 3) return 'high';
    if (subscriptionDetails.days_remaining <= 5) return 'medium';
    return null;
  };

  const handleRenewalSuccess = async () => {
    await fetchSubscriptionDetails();
    checkSubscriptionStatus();
  };

  if (isChecking || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Vendor Dashboard</h1>
            <p className="text-sm text-gray-600">Welcome back, {user?.name || 'Vendor'}!</p>
          </div>
          {hasActiveSubscription && subscriptionDetails && (
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium mt-4 sm:mt-0"
            >
              <Crown className="w-4 h-4 text-primary-600" />
              <span>View Subscription</span>
            </button>
          )}
        </div>

        {/* Subscription Expiring Warning */}
        {hasActiveSubscription && subscriptionDetails && isExpiringSoon() && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            getUrgencyLevel() === 'critical' 
              ? 'bg-red-50 border-red-500' 
              : getUrgencyLevel() === 'high'
              ? 'bg-orange-50 border-orange-500'
              : 'bg-yellow-50 border-yellow-500'
          }`}>
            <div className="flex items-start space-x-3">
              <Bell className={`h-5 w-5 flex-shrink-0 ${
                getUrgencyLevel() === 'critical' 
                  ? 'text-red-500' 
                  : getUrgencyLevel() === 'high'
                  ? 'text-orange-500'
                  : 'text-yellow-500'
              } animate-pulse`} />
              <div className="flex-1">
                <h3 className={`text-sm font-medium ${
                  getUrgencyLevel() === 'critical' 
                    ? 'text-red-800' 
                    : getUrgencyLevel() === 'high'
                    ? 'text-orange-800'
                    : 'text-yellow-800'
                }`}>
                  {getUrgencyLevel() === 'critical' 
                    ? '⚠️ Critical: Subscription Expires Tomorrow!' 
                    : getUrgencyLevel() === 'high'
                    ? '🚨 High Priority: Subscription Expiring Soon!'
                    : '⚠️ Subscription Expiring Soon!'
                  }
                </h3>
                <p className={`text-sm mt-1 ${
                  getUrgencyLevel() === 'critical' 
                    ? 'text-red-700' 
                    : getUrgencyLevel() === 'high'
                    ? 'text-orange-700'
                    : 'text-yellow-700'
                }`}>
                  Your subscription expires in{' '}
                  <span className="font-semibold">
                    {subscriptionDetails.days_remaining} day{subscriptionDetails.days_remaining !== 1 ? 's' : ''}
                  </span>
                  . Renew now to avoid service interruption.
                </p>
                <button
                  onClick={() => setShowRenewalModal(true)}
                  className={`mt-3 inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    getUrgencyLevel() === 'critical' 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : getUrgencyLevel() === 'high'
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-yellow-600 text-white hover:bg-yellow-700'
                  }`}
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Renew Subscription
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="mb-6">
            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
              />
            ))}
          </div>
        )}

        {/* Key Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_bookings || analytics.total_bookings || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.completed_bookings || 0} completed
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(stats.total_revenue || analytics.total_revenue || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Average Rating</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.average_rating ? Number(analytics.average_rating).toFixed(1) : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.total_reviews || stats.total_reviews || 0} reviews
                </p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Bookings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.pending_bookings || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Requires attention</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Recent Bookings */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
              <Link
                to="/vendor/bookings"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {bookingsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : recentBookings.length > 0 ? (
              <div className="space-y-3">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.id}
                    onClick={() => navigate(`/vendor/bookings/${booking.id}`)}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">
                          {booking.booking_reference || `#${booking.id.substring(0, 8)}`}
                        </p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {booking.client && (
                          <span>{booking.client.name}</span>
                        )}
                        {booking.event_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(booking.event_date)}
                          </span>
                        )}
                        {booking.vendor_payout_amount && (
                          <span className="font-medium text-gray-900">
                            {formatCurrency(booking.vendor_payout_amount)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent bookings</p>
              </div>
            )}
          </div>

          {/* Quick Actions & Subscription Summary */}
          <div className="space-y-6">
            {/* Subscription Summary Card */}
            {hasActiveSubscription && subscriptionDetails && (
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-primary-600" />
                    <h3 className="font-semibold text-gray-900">Subscription</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/vendor/plan"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Plan Features
                    </Link>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setShowSubscriptionModal(true)}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Current Plan</p>
                    <p className="text-lg font-bold text-primary-600">{subscriptionDetails.plan_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      subscriptionDetails.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscriptionDetails.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Days Remaining</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {subscriptionDetails.days_remaining ?? 0} days
                    </p>
                  </div>
                  {subscriptionDetails.usage && subscriptionDetails.limits && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-600 mb-2">Plan Usage</p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Bookings this month</span>
                          <span className={subscriptionDetails.at_booking_limit ? 'font-medium text-amber-600' : 'text-gray-900'}>
                            {subscriptionDetails.usage.bookings_this_month}
                            {subscriptionDetails.limits.max_bookings_per_month != null
                              ? ` / ${subscriptionDetails.limits.max_bookings_per_month}`
                              : ' (unlimited)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Services</span>
                          <span className={subscriptionDetails.at_service_limit ? 'font-medium text-amber-600' : 'text-gray-900'}>
                            {subscriptionDetails.usage.services_count}
                            {subscriptionDetails.limits.max_services != null
                              ? ` / ${subscriptionDetails.limits.max_services}`
                              : ' (unlimited)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Portfolio items</span>
                          <span className={subscriptionDetails.at_portfolio_limit ? 'font-medium text-amber-600' : 'text-gray-900'}>
                            {subscriptionDetails.usage.portfolio_count}
                            {subscriptionDetails.limits.max_portfolio_items != null
                              ? ` / ${subscriptionDetails.limits.max_portfolio_items}`
                              : ' (unlimited)'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/vendor/bookings"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">View All Bookings</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  to="/vendor/services"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">Manage Services</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  to="/vendor/analytics"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">View Analytics</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  to="/vendor/payments"
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">Payment History</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Overview */}
        {analytics && Object.keys(analytics).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Analytics Overview</h2>
              <div className="w-48">
                <CustomSelect
                  value={period}
                  onChange={(value) => setPeriod(value)}
                  options={[
                    { value: 'week', label: 'This Week' },
                    { value: 'month', label: 'This Month' },
                    { value: 'quarter', label: 'This Quarter' },
                    { value: 'year', label: 'This Year' }
                  ]}
                  size="sm"
                  className="w-full"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Bookings ({period})</p>
                <p className="text-xl font-bold text-gray-900">{analytics.total_bookings || 0}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Revenue ({period})</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(analytics.total_revenue || 0)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Avg Booking Value</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(analytics.average_booking_value || 0)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
                <p className="text-xl font-bold text-gray-900">
                  {analytics.booking_conversion_rate 
                    ? `${Number(analytics.booking_conversion_rate).toFixed(1)}%` 
                    : '0%'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Booking Status Breakdown */}
        {stats && Object.keys(stats).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Status</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{stats.pending_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Pending</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{stats.confirmed_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Confirmed</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{stats.in_progress_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">In Progress</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stats.completed_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Completed</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{stats.cancelled_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Cancelled</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{stats.total_bookings || 0}</p>
                <p className="text-xs text-gray-600 mt-1">Total</p>
              </div>
            </div>
          </div>
        )}

        {/* Renewal Modal */}
        <RenewalModal
          isOpen={showRenewalModal}
          onClose={() => setShowRenewalModal(false)}
          subscriptionDetails={subscriptionDetails}
          onSuccess={handleRenewalSuccess}
        />

        {/* Subscription Details Modal */}
        <SubscriptionDetailsModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          subscriptionDetails={subscriptionDetails}
          onRefresh={fetchSubscriptionDetails}
        />
      </div>
    </div>
  );
};

export default VendorDashboard;
